/**
 * DNI Attribution Page
 * Dynamic Number Insertion tracking and campaign attribution
 * Multi-touch attribution, campaign performance, and ROI reporting
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Card } from '@/components/ui/card';
import { Megaphone, Phone, TrendingUp, DollarSign } from 'lucide-react';

interface Campaign {
  id: string;
  name: string;
  phone_number: string;
  source: string;
  medium: string;
  total_calls: number;
  conversions: number;
  conversion_rate: number;
  avg_call_value: number;
  total_revenue: number;
  roi: number;
}

interface Attribution {
  touchpoint: string;
  calls: number;
  conversions: number;
  contribution: number;
}

export default function DNIAttribution() {
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'quarter'>('month');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [attributionData, setAttributionData] = useState<Attribution[]>([]);
  const [topSources, setTopSources] = useState<any[]>([]);
  const [phoneNumbers, setPhoneNumbers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAttributionData();
  }, [dateRange]);

  const loadAttributionData = async () => {
    setLoading(true);
    await Promise.all([
      loadCampaigns(),
      loadMultiTouchAttribution(),
      loadTopSources(),
      loadPhoneNumbers()
    ]);
    setLoading(false);
  };

  const getDateRange = () => {
    const end = new Date();
    const start = new Date();
    
    switch (dateRange) {
      case 'week':
        start.setDate(start.getDate() - 7);
        break;
      case 'month':
        start.setDate(start.getDate() - 30);
        break;
      case 'quarter':
        start.setDate(start.getDate() - 90);
        break;
    }
    
    return { start: start.toISOString(), end: end.toISOString() };
  };

  const loadCampaigns = async () => {
    const { start, end } = getDateRange();
    
    // Query calls with metadata containing campaign info
    const { data: callsData } = await supabase
      .from('calls')
      .select('to_number, disposition, metadata')
      .gte('started_at', start)
      .lte('started_at', end);
    
    if (callsData) {
      // Group by tracking phone number and extract campaign info
      const campaignMap = new Map<string, any>();
      
      callsData.forEach((call: any) => {
        const phoneNumber = call.to_number;
        const campaign = call.metadata?.campaign || 'Direct';
        const source = call.metadata?.utm_source || 'Unknown';
        const medium = call.metadata?.utm_medium || 'Unknown';
        const callValue = parseFloat(call.metadata?.estimated_value || '0');
        
        const key = `${phoneNumber}_${campaign}`;
        
        if (!campaignMap.has(key)) {
          campaignMap.set(key, {
            id: key,
            name: campaign,
            phone_number: phoneNumber,
            source,
            medium,
            total_calls: 0,
            conversions: 0,
            total_revenue: 0,
            call_values: []
          });
        }
        
        const campaignData = campaignMap.get(key);
        campaignData.total_calls++;
        
        if (call.disposition === 'resolved') {
          campaignData.conversions++;
          campaignData.total_revenue += callValue;
        }
        
        campaignData.call_values.push(callValue);
      });
      
      // Calculate metrics
      const campaignsArray = Array.from(campaignMap.values()).map((campaign: any) => ({
        ...campaign,
        conversion_rate: campaign.total_calls > 0 
          ? (campaign.conversions / campaign.total_calls * 100)
          : 0,
        avg_call_value: campaign.call_values.length > 0
          ? campaign.call_values.reduce((sum: number, val: number) => sum + val, 0) / campaign.call_values.length
          : 0,
        roi: 0 // Would need campaign spend data to calculate actual ROI
      }));
      
      setCampaigns(campaignsArray.sort((a, b) => b.total_revenue - a.total_revenue));
    }
  };

  const loadMultiTouchAttribution = async () => {
    const { start, end } = getDateRange();
    
    // In a real implementation, this would track multiple touchpoints
    // For now, we'll simulate multi-touch attribution models
    const { data: callsData } = await supabase
      .from('calls')
      .select('disposition, metadata')
      .gte('started_at', start)
      .lte('started_at', end);
    
    if (callsData) {
      const touchpoints = new Map<string, { calls: number; conversions: number }>();
      
      callsData.forEach((call: any) => {
        const source = call.metadata?.utm_source || 'Direct';
        
        if (!touchpoints.has(source)) {
          touchpoints.set(source, { calls: 0, conversions: 0 });
        }
        
        const data = touchpoints.get(source)!;
        data.calls++;
        
        if (call.disposition === 'resolved') {
          data.conversions++;
        }
      });
      
      const total = callsData.length;
      const attributionArray = Array.from(touchpoints.entries()).map(([touchpoint, data]) => ({
        touchpoint,
        calls: data.calls,
        conversions: data.conversions,
        contribution: total > 0 ? (data.calls / total * 100) : 0
      }));
      
      setAttributionData(attributionArray.sort((a, b) => b.contribution - a.contribution));
    }
  };

  const loadTopSources = async () => {
    const { start, end } = getDateRange();
    
    const { data } = await supabase
      .from('calls')
      .select('disposition, metadata')
      .gte('started_at', start)
      .lte('started_at', end);
    
    if (data) {
      const sources = new Map<string, { calls: number; conversions: number }>();
      
      data.forEach((call: any) => {
        const source = call.metadata?.utm_source || 'Direct';
        
        if (!sources.has(source)) {
          sources.set(source, { calls: 0, conversions: 0 });
        }
        
        const sourceData = sources.get(source)!;
        sourceData.calls++;
        
        if (call.disposition === 'resolved') {
          sourceData.conversions++;
        }
      });
      
      const sourcesArray = Array.from(sources.entries())
        .map(([name, data]) => ({
          name,
          calls: data.calls,
          conversions: data.conversions,
          rate: data.calls > 0 ? (data.conversions / data.calls * 100).toFixed(1) : '0.0'
        }))
        .sort((a, b) => b.calls - a.calls)
        .slice(0, 5);
      
      setTopSources(sourcesArray);
    }
  };

  const loadPhoneNumbers = async () => {
    // Load tracking phone numbers
    // In a real implementation, this would come from a tracking numbers table
    const { start, end } = getDateRange();
    
    const { data } = await supabase
      .from('calls')
      .select('to_number')
      .gte('started_at', start)
      .lte('started_at', end);
    
    if (data) {
      const numberMap = new Map<string, number>();
      
      data.forEach((call: any) => {
        numberMap.set(call.to_number, (numberMap.get(call.to_number) || 0) + 1);
      });
      
      const numbersArray = Array.from(numberMap.entries())
        .map(([number, calls]) => ({ number, calls }))
        .sort((a, b) => b.calls - a.calls);
      
      setPhoneNumbers(numbersArray);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-4xl mb-4">📊</div>
          <div className="text-gray-600">Loading attribution data...</div>
        </div>
      </div>
    );
  }

  return (
    <AdminPageLayout
      title="DNI Attribution"
      subtitle="Campaign tracking and multi-touch attribution"
      actions={
        <>
          {(['week', 'month', 'quarter'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                dateRange === range
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {range === 'week' ? '7 Days' : range === 'month' ? '30 Days' : '90 Days'}
            </button>
          ))}
        </>
      }
    >
      {/* Summary Metrics */}
      <StatsGrid cols={4}>
        <StatCard
          label="Total Campaigns"
          value={campaigns.length}
          icon={Megaphone}
        />
        <StatCard
          label="Total Calls"
          value={campaigns.reduce((sum, c) => sum + c.total_calls, 0)}
          icon={Phone}
        />
        <StatCard
          label="Avg Conversion Rate"
          value={`${campaigns.length > 0
            ? (campaigns.reduce((sum, c) => sum + c.conversion_rate, 0) / campaigns.length).toFixed(1)
            : '0.0'}%`}
          icon={TrendingUp}
        />
        <StatCard
          label="Total Revenue"
          value={`$${campaigns.reduce((sum, c) => sum + c.total_revenue, 0).toLocaleString()}`}
          icon={DollarSign}
        />
      </StatsGrid>

      {/* Campaign Performance Table */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Campaign Performance</h2>
        <div className="overflow-x-auto -mx-6">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Medium</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Calls</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conversions</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conv. Rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Value</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {campaigns.map((campaign) => (
                <tr key={campaign.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{campaign.phone_number}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {campaign.source}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                      {campaign.medium}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {campaign.total_calls}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {campaign.conversions}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      campaign.conversion_rate >= 30 ? 'bg-green-100 text-green-800' :
                      campaign.conversion_rate >= 15 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {campaign.conversion_rate.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${campaign.avg_call_value.toFixed(0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    ${campaign.total_revenue.toLocaleString()}
                  </td>
                </tr>
              ))}
              {campaigns.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-6 py-8 text-center text-gray-400">
                    No campaign data available for this period
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Two Column Layout */}
      <div className="grid grid-cols-2 gap-6">
        {/* Multi-Touch Attribution */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Multi-Touch Attribution</h2>
          <div>
            <div className="space-y-4">
              {attributionData.map((attr) => (
                <div key={attr.touchpoint}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-sm font-medium text-gray-900">{attr.touchpoint}</span>
                      <span className="text-xs text-gray-500 ml-2">
                        {attr.calls} calls • {attr.conversions} conversions
                      </span>
                    </div>
                    <span className="text-sm font-bold text-gray-900">
                      {attr.contribution.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${attr.contribution}%` }}
                    />
                  </div>
                </div>
              ))}
              {attributionData.length === 0 && (
                <div className="text-center text-gray-400 py-8">
                  No attribution data available
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Top Traffic Sources */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Traffic Sources</h2>
          <div>
            <div className="space-y-3">
              {topSources.map((source, index) => (
                <div key={source.name} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold ${
                      index === 0 ? 'bg-yellow-400 text-yellow-900' :
                      index === 1 ? 'bg-gray-300 text-gray-700' :
                      index === 2 ? 'bg-orange-300 text-orange-900' :
                      'bg-gray-200 text-gray-600'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{source.name}</div>
                      <div className="text-xs text-gray-500">{source.calls} calls • {source.conversions} conversions</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">{source.rate}%</div>
                    <div className="text-xs text-gray-500">Conv. Rate</div>
                  </div>
                </div>
              ))}
              {topSources.length === 0 && (
                <div className="text-center text-gray-400 py-8">
                  No source data available
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Tracking Phone Numbers */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Tracking Phone Numbers</h2>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
            + Add Tracking Number
          </button>
        </div>
        <div>
          <div className="grid grid-cols-3 gap-4">
            {phoneNumbers.map((phone) => (
              <div key={phone.number} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-lg font-mono font-bold text-gray-900">{phone.number}</div>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Active
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>Total Calls:</span>
                    <span className="font-medium">{phone.calls}</span>
                  </div>
                </div>
              </div>
            ))}
            {phoneNumbers.length === 0 && (
              <div className="col-span-3 text-center text-gray-400 py-8">
                No tracking numbers configured
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* ROI Reporting */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">ROI Reporting</h2>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start space-x-4">
            <div className="text-4xl">💰</div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2">ROI Calculation Coming Soon</h3>
              <p className="text-sm text-gray-600 mb-4">
                To enable full ROI reporting, integrate your advertising spend data. This will allow you to calculate:
              </p>
              <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                <li>Cost per call by campaign</li>
                <li>Cost per conversion</li>
                <li>Return on ad spend (ROAS)</li>
                <li>Customer acquisition cost (CAC)</li>
                <li>Lifetime value vs. acquisition cost</li>
              </ul>
              <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                Configure Ad Spend Integration
              </button>
            </div>
          </div>
        </div>
      </Card>
    </AdminPageLayout>
  );
}
